import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  Building2Icon,
  CheckIcon,
  CheckCircle2Icon,
  InfoIcon,
  RotateCcwIcon,
  UsersRoundIcon,
} from 'lucide-react';
import { BrandMark } from './BrandMark';
import { submitCompanyApplication } from '../../hooks/useCompanyApplications';

const ease = [0.22, 1, 0.36, 1] as const;

const companyBenefits = [
  'Samlad lägesbild för förare och fordon',
  'Tydligare dialog kring vilotid och pass',
  'Ett lugnare underlag inför kontroll',
];

const empty = {
  company_name: '',
  org_number: '',
  address: '',
  postal_code: '',
  city: '',
  company_phone: '',
  company_email: '',
  owner_full_name: '',
  owner_email: '',
  owner_phone: '',
  fleet_size: '',
  notes: '',
};

type Field = {
  label: string;
  key: keyof typeof empty;
  type?: string;
  required?: boolean;
  full?: boolean;
  textarea?: boolean;
  placeholder?: string;
};

const fields: Field[] = [
  { label: 'Företagsnamn', key: 'company_name', required: true, full: true },
  { label: 'Org.nr', key: 'org_number' },
  { label: 'Antal fordon', key: 'fleet_size', type: 'number' },
  { label: 'Adress', key: 'address', full: true },
  { label: 'Postnummer', key: 'postal_code' },
  { label: 'Ort', key: 'city' },
  { label: 'Företagets telefon', key: 'company_phone' },
  { label: 'Företagets e-post', key: 'company_email', type: 'email' },
  { label: 'Ägare / kontakt · namn', key: 'owner_full_name', required: true },
  { label: 'Ägare · e-post', key: 'owner_email', type: 'email', required: true },
  { label: 'Ägare · telefon', key: 'owner_phone' },
  {
    label: 'Noteringar',
    key: 'notes',
    full: true,
    textarea: true,
    placeholder: 'Taxitrafiktillstånd, uppgifter om fordonen…',
  },
];

const RegisterScreen: React.FC = () => {
  const navigate = useNavigate();
  const [flipped, setFlipped] = useState(false);
  const [form, setForm] = useState({ ...empty });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const set = (key: keyof typeof empty, value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!form.company_name.trim() || !form.owner_full_name.trim() || !form.owner_email.trim()) {
      setError('Företagsnamn, ägarens namn och ägarens e-post krävs.');
      return;
    }
    setLoading(true);
    const { error: err } = await submitCompanyApplication({
      company_name: form.company_name.trim(),
      org_number: form.org_number.trim() || undefined,
      address: form.address.trim() || undefined,
      postal_code: form.postal_code.trim() || undefined,
      city: form.city.trim() || undefined,
      company_phone: form.company_phone.trim() || undefined,
      company_email: form.company_email.trim() || undefined,
      owner_full_name: form.owner_full_name.trim(),
      owner_email: form.owner_email.trim(),
      owner_phone: form.owner_phone.trim() || undefined,
      fleet_size: form.fleet_size ? Number(form.fleet_size) : undefined,
      notes: form.notes.trim() || undefined,
    });
    setLoading(false);
    if (err) {
      setError(err);
      return;
    }
    setSubmitted(true);
  }

  return (
    <div className="ivilotid-site min-h-screen bg-[#F4F8FA] text-[#0B1120]">
      <header className="mx-auto flex h-[72px] max-w-6xl items-center justify-between px-5 sm:px-6 lg:px-8">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="group inline-flex min-h-10 items-center gap-2 rounded-lg px-1 text-sm font-semibold text-slate-700 hover:text-[#007EAF] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#06B6D4]"
        >
          <ArrowLeftIcon size={16} className="transition-transform group-hover:-translate-x-0.5" aria-hidden="true" />
          Tillbaka
        </button>
        <BrandMark compact />
      </header>

      <main tabIndex={-1} className="mx-auto flex min-h-[calc(100vh-72px)] max-w-3xl items-center px-4 py-6 sm:px-5 lg:px-8">
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease }}
          className="grid w-full overflow-hidden rounded-2xl border border-[#0B1120]/10 bg-white shadow-[0_10px_28px_rgba(11,17,32,.06)] lg:grid-cols-[.82fr_1.18fr]"
        >
          <aside className="relative overflow-hidden bg-[#06B6D4] p-5 text-[#071A28] sm:p-5">
            <Building2Icon size={26} strokeWidth={1.5} aria-hidden="true" className="relative" />
            <p className="relative mt-4 text-xs font-bold uppercase tracking-[.12em] text-[#0B3550]">
              För taxiföretag
            </p>
            <h1 className="relative mt-2 max-w-md font-display text-xl leading-[1.1] tracking-[-.04em] sm:text-2xl">
              Börja med en tydligare lägesbild.
            </h1>
            <p className="relative mt-3 max-w-sm text-sm leading-6 text-[#072C42]">
              Skapa en gemensam startpunkt för förare, fordon och pass — från det
              första uppdraget till överblicken efteråt.
            </p>
            <ul className="relative mt-4 space-y-2" aria-label="Fördelar för taxiföretag">
              {companyBenefits.map((benefit, index) => (
                <motion.li
                  key={benefit}
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.24, delay: 0.08 + index * 0.05, ease }}
                  className="flex items-start gap-2.5 text-sm leading-6 text-[#072C42]"
                >
                  <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[#071A28] text-[#A5F3FC]">
                    <CheckIcon size={12} strokeWidth={3} aria-hidden="true" />
                  </span>
                  {benefit}
                </motion.li>
              ))}
            </ul>
          </aside>

          {/* Flip panel: CTA (front) <-> form (back) */}
          <div
            className="relative grid min-h-[300px] p-5 sm:min-h-[320px] sm:p-6"
            style={{ perspective: '1800px' }}
          >
            {submitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, ease }}
                className="col-start-1 row-start-1 flex flex-col items-center justify-center px-2 py-4 text-center"
              >
                <span className="grid h-12 w-12 place-items-center rounded-xl bg-[#DCFCE7] text-[#166534]">
                  <CheckCircle2Icon size={26} aria-hidden="true" />
                </span>
                <h2 className="mt-3 font-display text-xl tracking-[-.03em]">Ansökan skickad.</h2>
                <p className="mt-2 max-w-sm text-sm leading-6 text-slate-700">
                  iVilotid granskar din registrering. Du får
                  inloggningsuppgifter via e-post när företaget godkänts.
                </p>
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="mt-5 inline-flex min-h-10 items-center gap-2 rounded-lg bg-[#0B1120] px-4 text-sm font-bold text-white hover:bg-[#163049] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#06B6D4]"
                >
                  Till inloggning
                  <ArrowRightIcon size={16} aria-hidden="true" />
                </button>
              </motion.div>
            ) : (
              <>
                {/* FRONT — CTA */}
                <motion.div
                  className="col-start-1 row-start-1"
                  style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden', pointerEvents: flipped ? 'none' : 'auto' }}
                  initial={false}
                  animate={{ rotateY: flipped ? 180 : 0 }}
                  transition={{ duration: 0.7, ease }}
                >
                  <div className="flex h-full flex-col justify-center py-1">
                    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[.12em] text-[#006D98]">
                      <UsersRoundIcon size={15} aria-hidden="true" />
                      Registrera företag
                    </div>
                    <h2 className="mt-2 font-display text-xl leading-[1.15] tracking-[-.03em] sm:text-2xl">
                      Ett konto för hela fordonsparken.
                    </h2>
                    <p className="mt-3 max-w-lg text-sm leading-6 text-slate-700">
                      Fyll i företagets uppgifter så påbörjar vi din registrering.
                      Det tar någon minut — vänd kortet för att börja.
                    </p>
                    <motion.button
                      type="button"
                      onClick={() => setFlipped(true)}
                      whileHover={{ scale: 1.015 }}
                      whileTap={{ scale: 0.98 }}
                      transition={{ duration: 0.14 }}
                      className="group mt-4 inline-flex min-h-10 w-full items-center justify-center gap-2.5 rounded-lg bg-[#06B6D4] px-4 text-sm font-bold text-[#071A28] shadow-[0_5px_12px_rgba(6,182,212,.18)] hover:bg-[#22D3EE] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#007EAF] focus-visible:ring-offset-2 sm:w-auto"
                    >
                      Registrera
                      <ArrowRightIcon size={17} className="transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                    </motion.button>
                    <p className="mt-4 text-sm text-slate-600">
                      Har du redan ett konto?{' '}
                      <button type="button" onClick={() => navigate('/login')} className="font-bold text-[#007EAF] hover:text-[#06B6D4]">
                        Logga in
                      </button>
                    </p>
                    <p className="mt-2 text-xs text-slate-500">
                      Förare: använd <strong>iVilotid</strong>-appen med din förarkod.
                    </p>
                  </div>
                </motion.div>

                {/* BACK — form */}
                <motion.div
                  className="col-start-1 row-start-1 flex max-h-[min(72vh,520px)] flex-col"
                  style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden', pointerEvents: flipped ? 'auto' : 'none' }}
                  initial={false}
                  animate={{ rotateY: flipped ? 360 : 180 }}
                  transition={{ duration: 0.7, ease }}
                >
                  <div className="flex shrink-0 items-center justify-between gap-3">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[.12em] text-[#006D98]">Företagsansökan</p>
                      <h2 className="mt-0.5 font-display text-xl tracking-[-.035em]">Fyll i uppgifterna.</h2>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFlipped(false)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-[#0B1120]/12 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:border-[#06B6D4] hover:text-[#007EAF]"
                    >
                      <RotateCcwIcon size={13} aria-hidden="true" />
                      Vänd
                    </button>
                  </div>

                  <AnimatePresence>
                    {error ? (
                      <motion.div
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="mt-3 flex gap-2.5 rounded-xl border border-red-200 bg-red-50 p-2.5 text-sm leading-6 text-red-700"
                      >
                        <InfoIcon className="mt-0.5 shrink-0" size={17} aria-hidden="true" />
                        <p>{error}</p>
                      </motion.div>
                    ) : null}
                  </AnimatePresence>

                  <motion.form
                    onSubmit={handleSubmit}
                    className="mt-3 grid flex-1 grid-cols-1 gap-2.5 overflow-y-auto pr-1 sm:grid-cols-2"
                    initial="hidden"
                    animate={flipped ? 'visible' : 'hidden'}
                    variants={{ visible: { transition: { staggerChildren: 0.035, delayChildren: 0.35 } } }}
                  >
                    {fields.map((f) => (
                      <motion.label
                        key={f.key}
                        variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0, transition: { duration: 0.28, ease } } }}
                        className={`block text-xs font-semibold text-slate-700 sm:text-sm ${f.full ? 'sm:col-span-2' : ''}`}
                      >
                        {f.label}{f.required ? ' *' : ''}
                        {f.textarea ? (
                          <textarea
                            value={form[f.key]}
                            onChange={(e) => set(f.key, e.target.value)}
                            placeholder={f.placeholder}
                            className="mt-1 min-h-[64px] w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-[#0B1120] outline-none placeholder:text-slate-400 focus:border-[#06B6D4] focus:ring-4 focus:ring-[#06B6D4]/15"
                          />
                        ) : (
                          <input
                            type={f.type ?? 'text'}
                            value={form[f.key]}
                            onChange={(e) => set(f.key, e.target.value)}
                            required={f.required}
                            className="mt-1 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-[#0B1120] outline-none placeholder:text-slate-400 focus:border-[#06B6D4] focus:ring-4 focus:ring-[#06B6D4]/15"
                          />
                        )}
                      </motion.label>
                    ))}

                    <motion.button
                      type="submit"
                      disabled={loading}
                      variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
                      whileHover={{ scale: loading ? 1 : 1.01 }}
                      whileTap={{ scale: 0.98 }}
                      className="group mt-0.5 flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-[#06B6D4] text-sm font-bold text-[#071A28] shadow-[0_5px_12px_rgba(6,182,212,.18)] hover:bg-[#22D3EE] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#007EAF] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 sm:col-span-2"
                    >
                      {loading ? (
                        <>
                          <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#071A28]/30 border-t-[#071A28]" />
                          Skickar…
                        </>
                      ) : (
                        <>
                          Skicka ansökan
                          <ArrowRightIcon size={17} className="transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                        </>
                      )}
                    </motion.button>
                  </motion.form>
                </motion.div>
              </>
            )}
          </div>
        </motion.section>
      </main>
    </div>
  );
};

export default RegisterScreen;
